import { jsx, jsxs } from 'react/jsx-runtime';

/**
 * Renders a selectable address option in a dropdown list.
 *
 * @component
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - Additional content to render after the address option item
 * @param {AddressSearchResultWithSubBuildings} props.address - The address data to display in this option
 * @param {AddressSearchResultWithSubBuildings | undefined} props.selectedOption - The currently selected address option
 * @param {(address: AddressSearchResultWithSubBuildings) => void} props.selectAddress - Callback function invoked when the option is selected via click or keyboard
 *
 * @returns {JSX.Element} A div element with role="option" containing the formatted address information and children
 *
 * @example
 * ```tsx
 * <Option
 *   address={addressResult}
 *   selectedOption={currentSelection}
 *   selectAddress={handleAddressSelect}
 * >
 *   <CustomContent />
 * </Option>
 * ```
 *
 * @remarks
 * - Supports keyboard interaction (Enter and Space keys) and mouse clicks
 * - Displays address line 1, city, state code, and postal code
 * - Shows unit count for addresses with sub-buildings
 * - Implements WAI-ARIA patterns for accessible list options
 */
const styles = {
    option: 'w-full flex flex-col items-start text-sm font-medium p-2',
    optionItemContainer: 'flex flex-row justify-between items-center',
    optionContainer: (address, selectedOptionId) => `px-4 py-2 hover:bg-primary-100 cursor-pointer ${selectedOptionId === address.id ? 'bg-primary-100' : ''}`,
};
const Option = ({ children, address, selectedOption, selectAddress, }) => {
    const optionItem = () => (jsxs("div", { className: styles['option'], children: [jsx("p", { className: "text-sm font-medium", children: address.subBuilding && address.subBuilding.length
                    ? `${address.addressLine1} - ${address.totalUnits} addresses found`
                    : address.addressLine1 }), jsx("p", { className: "text-xs text-neutral-900", children: address.city
                    ? `${address.city}, ${address.regionOrStateCode}, ${address.postalCode ?? ''}`
                    : address.address })] }));
    return (jsx("div", { role: "option", "aria-selected": selectedOption?.id === address.id, tabIndex: 0, className: styles['optionContainer'](address, selectedOption?.id), onClick: () => {
            selectAddress(address);
        }, onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                selectAddress(address);
            }
        }, children: jsxs("div", { className: styles['optionItemContainer'], children: [optionItem(), children] }) }));
};

export { Option };
